import React, { useState, useEffect } from 'react';
import { Card } from '../common';
import { ContentItem, ContentType, DIFFICULTY_DISPLAY_NAMES, DIFFICULTIES } from '@memriti/shared';
import SafeHtmlRenderer from '../common/SafeHtmlRenderer';

type ContentDifficulty = typeof DIFFICULTIES[number];

interface ContentDisplayProps {
  content: ContentItem;
  onAnswer: (answer: string) => void;
  userAnswer: string;
  answerSubmitted?: boolean;
  isCorrect?: boolean;
  correctAnswer?: string;
  explanation?: string;
}

const DifficultyIndicator: React.FC<{ difficulty: ContentDifficulty }> = ({ difficulty }) => {
  const displayName = DIFFICULTY_DISPLAY_NAMES[difficulty];

  const getDifficultyColor = (level: ContentDifficulty) => {
    switch (level) {
      case DIFFICULTIES[0]: return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
      case DIFFICULTIES[1]: return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
      case DIFFICULTIES[2]: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
      case DIFFICULTIES[3]: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
      case DIFFICULTIES[4]: return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
      default: return '';
    }
  };

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(difficulty)}`}>
      {displayName}
    </div>
  );
};

export const ContentDisplay: React.FC<ContentDisplayProps> = ({
  content,
  onAnswer,
  userAnswer,
  answerSubmitted = false,
  isCorrect,
  correctAnswer,
  explanation
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string>(userAnswer);

  useEffect(() => {
    setSelectedAnswer(userAnswer);
  }, [userAnswer]);

  const handleAnswerChange = (answer: string) => {
    if (answerSubmitted) return;
    setSelectedAnswer(answer);
    onAnswer(answer);
  };

  const renderContent = () => {
    switch (content.type) {
      case ContentType.MCQ: {
        const payload = content.payload as any;
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold dark:text-white">Multiple Choice Question</h3>
                <DifficultyIndicator difficulty={content.difficulty} />
              </div>
              <div className="mb-6 dark:text-gray-300">
                <SafeHtmlRenderer html={payload.question} />
              </div>
              <div className="space-y-3">
                {payload.options.map((option: string, index: number) => {
                  const isUserAnswer = answerSubmitted && option === userAnswer;
                  const isCorrectAnswer = answerSubmitted && option === correctAnswer;

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerChange(option)}
                      className={`w-full p-4 text-left rounded-lg border ${
                        isUserAnswer
                          ? isCorrect
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                            : 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:text-white'
                          : isCorrectAnswer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                          : selectedAnswer === option
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:text-white'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-300 dark:bg-gray-800'
                      }`}
                      disabled={answerSubmitted}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {answerSubmitted && explanation && (
                <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold text-indigo-800 dark:text-indigo-200">Explanation</span>
                  </div>
                  <div className="text-slate-700 dark:text-slate-300 leading-relaxed">
                    <SafeHtmlRenderer html={explanation} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case ContentType.TRUE_FALSE: {
        const payload = content.payload as any;
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold dark:text-white">True or False</h3>
                <DifficultyIndicator difficulty={content.difficulty} />
              </div>
              <div className="mb-6 dark:text-gray-300">
                <SafeHtmlRenderer html={payload.statement} />
              </div>
              <div className="flex space-x-4">
                {['true', 'false'].map((option) => {
                  const isUserAnswer = answerSubmitted && option === userAnswer;
                  const isCorrectAnswer = answerSubmitted && option === correctAnswer;

                  return (
                    <button
                      key={option}
                      onClick={() => handleAnswerChange(option)}
                      className={`flex-1 p-4 rounded-lg border ${
                        isUserAnswer
                          ? isCorrect
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                            : 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:text-white'
                          : isCorrectAnswer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                          : selectedAnswer === option
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:text-white'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-300 dark:bg-gray-800'
                      }`}
                      disabled={answerSubmitted}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  );
                })}
              </div>
              {answerSubmitted && explanation && (
                <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold text-indigo-800 dark:text-indigo-200">Explanation</span>
                  </div>
                  <div className="text-slate-700 dark:text-slate-300 leading-relaxed">
                    <SafeHtmlRenderer html={explanation} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case ContentType.SELECT_MULTIPLE: {
        const payload = content.payload as any;
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold dark:text-white">Select Multiple</h3>
                <DifficultyIndicator difficulty={content.difficulty} />
              </div>
              <div className="mb-6 dark:text-gray-300">
                <SafeHtmlRenderer html={payload.question} />
              </div>
              <div className="space-y-3">
                {payload.options.map((option: string, index: number) => {
                  const isUserAnswer = answerSubmitted && option === userAnswer;
                  const isCorrectAnswer = answerSubmitted && option === correctAnswer;

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerChange(option)}
                      className={`w-full p-4 text-left rounded-lg border ${
                        isUserAnswer
                          ? isCorrect
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                            : 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:text-white'
                          : isCorrectAnswer
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:text-white'
                          : selectedAnswer === option
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:text-white'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-300 dark:bg-gray-800'
                      }`}
                      disabled={answerSubmitted}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {answerSubmitted && explanation && (
                <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold text-indigo-800 dark:text-indigo-200">Explanation</span>
                  </div>
                  <div className="text-slate-700 dark:text-slate-300 leading-relaxed">
                    <SafeHtmlRenderer html={explanation} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case ContentType.EXPLANATION:
      case ContentType.COMMON_MISTAKES:
      case ContentType.TIPS_TRICKS:
      case ContentType.EXAMPLE_QA:
        return null; // These content types don't require user input

      default:
        return null;
    }
  };

  return (
    <Card className="p-6">
      {renderContent()}
    </Card>
  );
};

export default ContentDisplay;